/**
 * Header — barra superior (visual Sphere).
 *
 * - Busca global (Cmd/Ctrl+K foca; Enter abre o primeiro resultado). Não há
 *   endpoint de busca global: o escopo é o das buscas que JÁ existem — telas do
 *   menu, projetos recentes (GET /dashboard/projects, mesmo cache do Dashboard) e
 *   agentes do catálogo (GET /agents?search=). Resultado vazio tem feedback.
 * - Notificações: os gates pendentes (KPI gateAprovacoes) com atalho para a fila
 *   de aprovação — é a única notificação que o produto tem hoje.
 * - Conta: usuário REAL do AuthContext (nome, papel, e-mail), Configurações,
 *   toggle global de animações e Sair.
 * - ≤640 px: botão de menu que abre a navegação (gaveta) — `onToggleMenu`.
 *
 * Acessibilidade: <header>; busca com role="search" e label; menus com Escape e
 * retorno de foco ao acionador.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Bell, ChevronDown, Menu, X, LogOut, Settings, Shield, FolderKanban, Bot, LayoutGrid, Sparkles,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { useApi } from '@/api/client';
import { RECENT_PROJECTS_KEY, fetchRecentProjects, useDashboardKpis } from '@/api/dashboard';
import { projectRoute } from '@/lib/projectRoute';
import { useMotionPreference } from '@/ui/sphere/motion';
import { usePopover, moverFocoNoMenu } from '@/ui/sphere/usePopover';
import { NAV_ITEMS } from './Sidebar';

/** Extrai as iniciais de um nome para o avatar. */
export function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const TELAS_DA_BUSCA = [
  ...NAV_ITEMS.map((n) => ({ id: n.id, label: n.label })),
  { id: 'gates', label: 'Aprovação de Gates' },
  { id: 'journey', label: 'Novo projeto (jornada)' },
];

function normalizar(s) {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function GlobalSearch({ navigate }) {
  const api = useApi();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [open, setOpen] = useState(false);

  // Cmd/Ctrl+K foca a busca de qualquer tela.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const ativo = open && dq.length > 0;
  const projetosQ = useQuery({
    queryKey: RECENT_PROJECTS_KEY,
    queryFn: () => fetchRecentProjects(api),
    enabled: ativo,
    staleTime: 30_000,
  });
  const agentesQ = useQuery({
    queryKey: ['agents', 'busca-global', dq],
    queryFn: () => api.get(`/agents?search=${encodeURIComponent(dq)}&limit=6`),
    enabled: ativo && dq.length >= 2,
    staleTime: 60_000,
  });

  const resultados = useMemo(() => {
    if (!dq) return [];
    const n = normalizar(dq);
    const telas = TELAS_DA_BUSCA.filter((t) => normalizar(t.label).includes(n))
      .map((t) => ({ key: `tela-${t.id}`, grupo: 'Telas', icon: LayoutGrid, titulo: t.label, destino: t.id }));
    const projetos = (projetosQ.data ?? [])
      .filter((p) => normalizar(p.name).includes(n) || normalizar(p.repo).includes(n))
      .slice(0, 6)
      .map((p) => ({ key: `proj-${p.id}`, grupo: 'Projetos', icon: FolderKanban, titulo: p.name, sub: p.repo, destino: projectRoute(p) }));
    const brutos = agentesQ.data;
    const lista = Array.isArray(brutos) ? brutos : (brutos?.items ?? []);
    const agentes = lista.slice(0, 6).map((a) => ({
      key: `ag-${a.id ?? a.name}`, grupo: 'Agentes', icon: Bot, titulo: a.name, sub: a.category,
      destino: `/catalog?search=${encodeURIComponent(a.name ?? '')}`,
    }));
    return [...telas, ...projetos, ...agentes];
  }, [dq, projetosQ.data, agentesQ.data]);

  const carregando = ativo && (projetosQ.isFetching || agentesQ.isFetching) && resultados.length === 0;

  const ir = (r) => {
    setOpen(false);
    setQ('');
    navigate(r.destino);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setOpen(true);
    if (resultados[0]) ir(resultados[0]);
  };

  let grupoAnterior = null;
  return (
    <div className="sp-search-wrap" ref={wrapRef}>
      <form className="sp-search" role="search" onSubmit={onSubmit}>
        <Search size={18} strokeWidth={1.75} aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); return; }
            if (e.key === 'ArrowDown' && listRef.current) {
              e.preventDefault();
              listRef.current.querySelector('button')?.focus();
            }
          }}
          aria-label="Buscar telas, projetos e agentes"
          aria-expanded={ativo}
          aria-controls="sp-busca-resultados"
          placeholder="Buscar projetos, agentes, telas..."
          autoComplete="off"
        />
        <kbd aria-hidden="true">⌘ K</kbd>
      </form>
      {ativo && (
        <div
          id="sp-busca-resultados"
          className="sp-popover sp-search-results"
          ref={listRef}
          role="region"
          aria-label="Resultados da busca"
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); inputRef.current?.focus(); return; }
            moverFocoNoMenu(e, listRef.current);
          }}
        >
          {carregando && <div className="sp-popover-empty" role="status">Buscando…</div>}
          {!carregando && resultados.length === 0 && (
            <div className="sp-popover-empty" role="status">Nenhum resultado para “{dq}”.</div>
          )}
          {resultados.map((r) => {
            const header = r.grupo !== grupoAnterior ? <div className="sp-popover-section">{r.grupo}</div> : null;
            grupoAnterior = r.grupo;
            return (
              <div key={r.key}>
                {header}
                <button type="button" className="sp-popover-item" onClick={() => ir(r)}>
                  <r.icon size={16} strokeWidth={1.75} aria-hidden="true" />
                  <span className="sp-popover-text">
                    <span>{r.titulo}</span>
                    {r.sub && <small>{r.sub}</small>}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Notifications({ navigate }) {
  const kpis = useDashboardKpis();
  const pop = usePopover();
  const n = Number.isFinite(Number(kpis.data?.gateAprovacoes)) ? Number(kpis.data?.gateAprovacoes) : null;
  const rotulo = n == null ? 'Notificações' : `Notificações: ${n} ${n === 1 ? 'gate pendente' : 'gates pendentes'}`;
  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={pop.triggerRef}
        type="button"
        className="sp-icon-button"
        aria-label={rotulo}
        aria-haspopup="true"
        aria-expanded={pop.open}
        onClick={pop.toggle}
      >
        <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
        {n > 0 && <span className="sp-badge-dot" aria-hidden="true" />}
      </button>
      {pop.open && (
        <div className="sp-popover sp-notif-menu" ref={pop.panelRef} role="region" aria-label="Notificações">
          <div className="sp-popover-section">Notificações</div>
          {kpis.isError && n == null ? (
            <div className="sp-popover-empty" role="status">Não foi possível carregar as notificações.</div>
          ) : n == null ? (
            <div className="sp-popover-empty" role="status">Carregando…</div>
          ) : n === 0 ? (
            <div className="sp-popover-empty" role="status">Nenhum gate aguardando aprovação.</div>
          ) : (
            <button type="button" className="sp-popover-item" onClick={() => { pop.close(false); navigate('gates'); }}>
              <Shield size={16} strokeWidth={1.75} aria-hidden="true" style={{ color: 'var(--sp-warning)' }} />
              <span className="sp-popover-text">
                <span>{n} {n === 1 ? 'gate aguardando aprovação' : 'gates aguardando aprovação'}</span>
                <small>Abrir a fila de aprovação</small>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AccountMenu({ navigate }) {
  const { user, logout } = useAuth();
  const { enabled: motion, setEnabled: setMotion } = useMotionPreference();
  const pop = usePopover();
  const displayName = user?.name || 'Usuário';
  const displayRole = user?.role || '';

  useEffect(() => {
    if (pop.open) pop.panelRef.current?.querySelector('[role^="menuitem"]')?.focus();
  }, [pop.open, pop.panelRef]);

  const handleLogout = () => {
    pop.close(false);
    logout();
    navigate('login');
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={pop.triggerRef}
        type="button"
        className="sp-account"
        aria-label={`Conta de ${displayName}`}
        aria-haspopup="menu"
        aria-expanded={pop.open}
        onClick={pop.toggle}
      >
        <span className="sp-avatar" aria-hidden="true">{getInitials(user?.name)}</span>
        <span className="sp-account-copy">
          <span>{displayName}</span>
          {displayRole && <small>{displayRole}</small>}
        </span>
        <ChevronDown className="sp-account-chevron" size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {pop.open && (
        <div
          className="sp-popover sp-account-menu"
          ref={pop.panelRef}
          role="menu"
          aria-label="Menu da conta"
          onKeyDown={(e) => moverFocoNoMenu(e, pop.panelRef.current)}
        >
          <div className="sp-popover-section" style={{ textTransform: 'none', letterSpacing: 0 }}>
            <div style={{ color: 'var(--sp-text)', fontSize: 13, fontWeight: 600 }}>{displayName}</div>
            {user?.email && <div style={{ fontSize: 12 }}>{user.email}</div>}
          </div>
          <button type="button" role="menuitem" className="sp-popover-item"
            onClick={() => { pop.close(false); navigate('settings'); }}>
            <Settings size={16} strokeWidth={1.75} aria-hidden="true" /> Configurações
          </button>
          <button type="button" role="menuitemcheckbox" aria-checked={motion} className="sp-popover-item"
            onClick={() => setMotion(!motion)}>
            <Sparkles size={16} strokeWidth={1.75} aria-hidden="true" />
            {motion ? 'Pausar animações' : 'Ativar animações'}
          </button>
          <button type="button" role="menuitem" className="sp-popover-item sp-popover-item--danger"
            onClick={handleLogout} aria-label="Sair da plataforma">
            <LogOut size={16} strokeWidth={1.75} aria-hidden="true" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

export function Header({ navigate, title, subtitle, hideTitle = false, navOpen = false, onToggleMenu, menuButtonRef }) {
  return (
    <>
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <button
            ref={menuButtonRef}
            type="button"
            className="sp-icon-button sp-mobile-menu"
            aria-label={navOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-controls="sp-navigation"
            aria-expanded={navOpen}
            onClick={onToggleMenu}
          >
            {navOpen ? <X size={20} strokeWidth={1.75} aria-hidden="true" /> : <Menu size={20} strokeWidth={1.75} aria-hidden="true" />}
          </button>
          <GlobalSearch navigate={navigate} />
        </div>
        <div className="sp-topbar-right">
          <Notifications navigate={navigate} />
          <AccountMenu navigate={navigate} />
        </div>
      </header>
      {!hideTitle && title && (
        <div className="sp-page-head">
          <div className="sp-page-title">{title}</div>
          {subtitle && <p>{subtitle}</p>}
        </div>
      )}
    </>
  );
}
